// app/features/customers/components/CustomerWorkflowAgentCardComponent.tsx
'use client';

import { Bot, Brain, Cog, LucideIcon, User, Users } from 'lucide-react';

import { PersonalizedUseCaseBaseCardComponent } from '@/app/features/ai-workflows/components/workflows/PersonalizedUseCaseBaseCardComponent';

import { CustomerWorkflowStep } from '../customers.interface';

interface CustomerWorkflowAgentCardProps {
  step: CustomerWorkflowStep;
  className?: string;
}

// Map actor to componentType that matches COMPONENT_TYPE_STYLES in base card
const actorToComponentType: Record<string, string> = {
  user: 'Trigger',           // orange
  ai_assistant: 'AIAgent',   // purple (THE MAIN ONE!)
  automation: 'HumanTask',   // teal
  human_review: 'DecisionNode', // yellow
};

const actorIcons: Record<string, LucideIcon> = {
  user: User,
  ai_assistant: Bot,
  automation: Cog,
  human_review: Users,
};

const actorLabels: Record<string, string> = {
  user: 'User Action',
  ai_assistant: 'AI Agent',
  automation: 'Automation',
  human_review: 'Human Review',
};

export function CustomerWorkflowAgentCardComponent({
  step,
  className = '',
}: CustomerWorkflowAgentCardProps) {
  const componentType = actorToComponentType[step.actor] || 'AIAgent';
  const IconComponent = actorIcons[step.actor] || Bot;
  const badgeText = actorLabels[step.actor] || 'AI Agent';

  return (
    <PersonalizedUseCaseBaseCardComponent
      title={step.title}
      description={step.description}
      componentType={componentType}
      icon={IconComponent}
      badgeText={badgeText}
      className={className}
    >
      {/* Core Agent Logic - LIKE THE REAL AGENT CARD */}
      {step.coreLogic && step.coreLogic.length > 0 && (
        <div className="my-4">
          <div className="flex items-center gap-2 mb-6">
            <h5 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <Brain size={16} className="text-purple-500" /> 
              Core Agent Logic
            </h5>
          </div>
          <div className="space-y-1.5 pl-2">
            {step.coreLogic.map((point, idx) => (
              <div key={idx} className="flex items-start gap-2 group">
                <div className="flex-shrink-0 w-4 h-4 rounded-sm border border-gray-400 flex items-center justify-center text-xs text-gray-800 font-mono mt-0.5">
                  {idx + 1}
                </div>
                <div className="text-base text-gray-800">{point}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inputs/Outputs */}
      <div className="pt-4">
        {/* Inputs Section */}
        {step.inputs && step.inputs.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h5 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono">
                Inputs
              </h5>
              <div className="h-px flex-grow bg-gray-300" />
            </div>
            <div className="flex flex-wrap gap-2">
              {step.inputs.map((input, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md font-medium"
                >
                  {input}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Outputs Section */}
        {step.outputs && step.outputs.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h5 className="text-xs font-semibold text-gray-800 uppercase tracking-wider font-mono">
                Outputs
              </h5>
              <div className="h-px flex-grow bg-gray-300" />
            </div>
            <div className="flex flex-wrap gap-2">
              {step.outputs.map((output, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1.5 bg-beige-dark text-gray-800 text-sm rounded-md font-medium"
                >
                  {output}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </PersonalizedUseCaseBaseCardComponent>
  );
}
